import type { ModelVersionId } from "../types";
import { WX_FINAL2 } from "./wx-final2/cellmap";
import wxFinal2Template from "./wx-final2/template.inputs.json";
import type { ModelInputs } from "../types";

type Range = [number, number];
type Table<C extends string> = { rows: Range; cols: Record<C, string> };

/**
 * Rótulo de la plantilla (encabezado o total) que el parser busca en la hoja real para ubicar cada tabla y celda
 * donde quedó, aunque el miembro haya insertado o borrado filas. Se compara sin espacios dobles ni selectores de emoji.
 */
export interface Landmark {
  id: string;
  /** Celda donde está en la plantilla. */
  cell: string;
  text: string;
  /** Basta con que el texto empiece así (rótulos largos que el modelo recorta o explica). */
  prefix?: boolean;
  /** Se repite en la hoja (SUBTOTAL de cada bucket): se busca solo entre los rótulos únicos que lo rodean. */
  unique?: false;
  /** Vive en una columna de datos (TOTAL en B): se toma la última coincidencia de su ventana, no la primera. */
  last?: true;
}
export type SheetKey = keyof VersionConfig["sheets"];
/** Tablas cuyo largo lo decide el Excel: el total que las cierra está en la fila siguiente a la última de la plantilla. */
export type ElasticTable = "assets" | "liabilities" | "objectives" | "incomes" | "expenses" | "team" | "filter" | "pres" | "inc" | "growth" | "projects" | "contrib";

export interface VersionConfig {
  id: ModelVersionId;
  label: string;
  catalogId: string;
  detect: { requiredSheets: string[]; signature: { sheet: string; cell: string; includes: string } };
  sheets: Record<
    "setup" | "diag" | "photo" | "identity" | "life" | "flow" | "balance" | "strategy" | "portfolio" | "system" | "portfoliosModel" | "assetsCatalog" | "catalog",
    string
  >;
  cells: {
    setup: { age: string; year: string; targetAge: string; currency: string };
    /** Lo que trae el Excel WX: pasos 3-5 pregunta por pregunta, pasos 1-2 como promedio y la Rueda como un número por área (viernes). */
    diag: { s3: string; s4: string; s5: string; s1Avg: string; s2Avg: string; wheelAvg: string[] };
    identity: { story: string[]; values: string[]; needs: Table<"need" | "me" | "partner" | "child1" | "child2">; archetype: string; archetype2: string; trap: string };
    life: {
      purpose: string[];
      film: Table<"phase" | "from" | "to" | "source" | "goals">;
      emergencyMonths: string;
      objectives: Table<"priority" | "name" | "age" | "type" | "costToday" | "strategyOverride">;
      emergencyStrategyOverride: string;
      freeCapitalStrategy: string;
    };
    flow: { incomes: Table<"source" | "amount" | "modality">; expenses: Table<"concept" | "category" | "amount" | "modality">; reflections: string[] };
    balance: { assets: Table<"what" | "category" | "subcategory" | "value" | "bucketOverride">; liabilities: Table<"what" | "balance" | "payment" | "rate" | "personalUse"> };
    strategy: { concreteAssets: string; misalignment: string; closeIn: string; manualRealRate: string; manualContribution: string };
    portfolio: { capitalOverride: string; buckets: Record<"pres" | "inc" | "growth" | "projects" | "contrib", Range>; cols: { category: string; subcategory: string; pct: string } };
    system: {
      team: Table<"role" | "has" | "name" | "satisfaction" | "cadence" | "need">;
      cadence: Table<"what" | "every" | "who" | "next">;
      filter: Table<"opportunity" | "bucket" | "objective" | "offered">;
      plan30: Table<"action" | "step" | "deadline" | "who">;
      courage: { act: string; area: string; when: string };
    };
  };
  rules: { cascadeBase: "gross" | "net"; portfolioBase: "gross" | "net"; excelProjectionRows: number };
  /** Rótulos por hoja, en orden de fila de la plantilla. Solo los que anclan una celda o cierran una tabla. */
  landmarks: Partial<Record<SheetKey, Landmark[]>>;
  /** Tabla elástica → id del rótulo que la cierra. */
  elastic: Record<ElasticTable, string>;
}

export const VERSIONS: Record<ModelVersionId, VersionConfig> = { "wx-final2": WX_FINAL2 };

export interface DetectResult { id: ModelVersionId | null; missingSheets: string[]; signatureOk: boolean }

/** Detecta la versión del modelo por hojas requeridas + celda firma. */
export function detectVersion(sheetNames: string[], readCell: (sheet: string, cell: string) => unknown): DetectResult {
  for (const cfg of Object.values(VERSIONS)) {
    const missing = cfg.detect.requiredSheets.filter((s) => !sheetNames.includes(s));
    const sig = readCell(cfg.detect.signature.sheet, cfg.detect.signature.cell);
    const signatureOk = typeof sig === "string" && sig.includes(cfg.detect.signature.includes);
    if (missing.length === 0) return { id: cfg.id, missingSheets: [], signatureOk };
    if (missing.length <= 2) return { id: null, missingSheets: missing, signatureOk };
  }
  return { id: null, missingSheets: VERSIONS["wx-final2"].detect.requiredSheets.filter((s) => !sheetNames.includes(s)), signatureOk: false };
}

/** Plantilla vacía de cada versión (lo que trae el Excel recién bajado: filas, conceptos y menús prellenados). Base del formulario "Mis datos". */
export const TEMPLATE_INPUTS: Record<ModelVersionId, ModelInputs> = { "wx-final2": wxFinal2Template as ModelInputs };
export const emptyInputs = (v: ModelVersionId = "wx-final2"): ModelInputs => JSON.parse(JSON.stringify(TEMPLATE_INPUTS[v])) as ModelInputs;
